import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, BellRinging, Check, Trash, BellSlash } from '@phosphor-icons/react';
import { useAuth } from '../App';
import {
  registerServiceWorker,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentSubscription,
} from '../lib/webPush';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 60 * 1000; // 1 minute

const NotificationBell = () => {
  const { user, API, language } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await API.get('/notifications/unread-count');
      setUnread(data.unread_count || 0);
    } catch (_) { /* ignore */ }
  }, [API, user]);

  const fetchItems = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await API.get('/notifications?limit=30');
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (_) { /* ignore */ }
  }, [API, user]);

  // Initial registration: SW + poll for unread + check push state
  useEffect(() => {
    if (!user) return;
    setPushSupported(isPushSupported());
    (async () => {
      await registerServiceWorker();
      const sub = await getCurrentSubscription();
      setPushEnabled(!!sub && Notification.permission === 'granted');
    })();
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, fetchUnread]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const togglePanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchItems();
  };

  const handleEnablePush = async () => {
    setBusy(true);
    try {
      const r = await subscribeToPush(API);
      if (r.ok) {
        setPushEnabled(true);
        toast.success(L('Phone notifications enabled', 'Telefon-Benachrichtigungen aktiviert'));
      } else if (r.reason === 'denied') {
        toast.error(L('Permission denied — enable in browser settings', 'Berechtigung verweigert — bitte in den Browser-Einstellungen aktivieren'));
      } else {
        toast.error(L('Could not enable push', 'Push konnte nicht aktiviert werden'));
      }
    } catch (e) {
      toast.error(L('Could not enable push', 'Push konnte nicht aktiviert werden'));
    } finally {
      setBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush(API);
      setPushEnabled(false);
      toast.success(L('Phone notifications disabled', 'Telefon-Benachrichtigungen deaktiviert'));
    } finally {
      setBusy(false);
    }
  };

  const markRead = async (id) => {
    try {
      await API.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await API.post('/notifications/mark-all-read');
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (_) { /* ignore */ }
  };

  const remove = async (id) => {
    try {
      await API.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch (_) { /* ignore */ }
  };

  const handleItemClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return L('just now', 'gerade eben');
    if (sec < 3600) return L(`${Math.floor(sec / 60)}m ago`, `vor ${Math.floor(sec / 60)} Min`);
    if (sec < 86400) return L(`${Math.floor(sec / 3600)}h ago`, `vor ${Math.floor(sec / 3600)} Std`);
    return d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: 'short' });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={togglePanel}
        className="relative p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        data-testid="notification-bell-btn"
        aria-label={L('Notifications', 'Benachrichtigungen')}
      >
        {unread > 0 ? (
          <BellRinging size={22} weight="duotone" className="text-emerald-600" />
        ) : (
          <Bell size={22} weight="duotone" />
        )}
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            data-testid="notification-unread-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 z-[10001]"
          data-testid="notification-panel"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">
              {L('Notifications', 'Benachrichtigungen')}
            </h3>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAllRead}
                className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                data-testid="notification-mark-all-read"
              >
                <Check size={14} className="inline mr-0.5" />
                {L('Mark all read', 'Alle gelesen')}
              </button>
            )}
          </div>

          {/* Push opt-in */}
          {pushSupported && (
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
              {pushEnabled ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-600 flex items-center gap-1.5">
                    <BellRinging size={14} className="text-emerald-600" weight="fill" />
                    {L('Phone push notifications: on', 'Telefon-Push: ein')}
                  </span>
                  <button
                    onClick={handleDisablePush}
                    disabled={busy}
                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                    data-testid="push-disable-btn"
                  >
                    {L('disable', 'deaktivieren')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleEnablePush}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold disabled:opacity-50"
                  data-testid="push-enable-btn"
                >
                  <BellRinging size={14} weight="fill" />
                  {L('Get notifications on this phone', 'Push auf diesem Gerät aktivieren')}
                </button>
              )}
            </div>
          )}

          <div className="max-h-[55vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <BellSlash size={28} className="text-slate-300 mx-auto mb-2" weight="duotone" />
                <p className="text-sm text-slate-400">
                  {L("No notifications yet", 'Noch keine Benachrichtigungen')}
                </p>
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`group px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${!n.read ? 'bg-emerald-50/40' : ''}`}
                  onClick={() => handleItemClick(n)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    )}
                    <div className={`flex-1 min-w-0 ${n.read ? 'opacity-70' : ''}`}>
                      <p className="text-sm font-semibold text-slate-900 leading-snug">{n.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">
                        {formatRelative(n.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                      className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`notification-delete-${n.id}`}
                      aria-label="delete"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
